import httpService from "./httpService";
import type {
  NotificationInboxItem,
  NotificationInboxListParams,
  NotificationInboxListResponse,
  NotificationMarkReadResponse,
} from "../types/notifications";

/**
 * Signed-in user's Web inbox (PWA-2689), wrapping the gateway routes under
 * `/v3/accounts/notifications` (plutus-backend
 * `routes/v3/notifications/accountNotificationRoutes.ts`).
 *
 * - Every call is actor-scoped (`requestScope: "actor"`): the inbox belongs to
 *   the session owner even while a delegated account is active.
 * - `surface=WEB` is fixed because the website is the WEB surface.
 * - Blank filters are never sent (an empty `categoryKey` is a 400 on the gateway).
 */

const BASE = "/v3/accounts/notifications";
const ACTOR_AUTH = { requiresAuth: true, requestScope: "actor" as const };
const MAX_PAGE_SIZE = 100;

const isPositiveSafeInteger = (value: number): boolean => Number.isSafeInteger(value) && value > 0;

const itemPath = (id: number): string => {
  if (!isPositiveSafeInteger(id)) throw new Error("notificationMessageId must be a positive integer");
  return `${BASE}/${encodeURIComponent(String(id))}`;
};

class NotificationInboxService {
  async list(params: NotificationInboxListParams = {}): Promise<NotificationInboxListResponse> {
    const query = new URLSearchParams({ surface: "WEB" });
    if (params.page !== undefined && isPositiveSafeInteger(params.page)) query.set("page", String(params.page));
    if (params.pageSize !== undefined && isPositiveSafeInteger(params.pageSize)) {
      query.set("pageSize", String(Math.min(MAX_PAGE_SIZE, params.pageSize)));
    }
    if (params.unread !== undefined) query.set("unread", params.unread ? "true" : "false");
    const categoryKey = params.categoryKey?.trim();
    if (categoryKey) query.set("categoryKey", categoryKey);
    const subcategoryKey = params.subcategoryKey?.trim();
    if (subcategoryKey) query.set("subcategoryKey", subcategoryKey);
    const response = await httpService.get<NotificationInboxListResponse>(`${BASE}?${query.toString()}`, ACTOR_AUTH);
    return response.data;
  }

  async get(id: number): Promise<NotificationInboxItem> {
    const response = await httpService.get<NotificationInboxItem>(`${itemPath(id)}?surface=WEB`, ACTOR_AUTH);
    return response.data;
  }

  /** Idempotent; the gateway rejects any body, so none is sent. */
  async markRead(id: number): Promise<NotificationMarkReadResponse> {
    const response = await httpService.put<NotificationMarkReadResponse>(
      `${itemPath(id)}/read?surface=WEB`,
      undefined,
      ACTOR_AUTH
    );
    return response.data;
  }
}

export const notificationInboxService = new NotificationInboxService();
export default notificationInboxService;
